/**
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from 'lit';
import './chromedash-callout.js';
import { GATE_TEAM_ORDER, GATE_FINISHED_REVIEW_STATES, PROGRESS_VOTE_STATE, PROGRESS_VOTE_STATE_NAMES, } from './form-field-enums.js';
import { makeDisplaySpec } from './form-field-specs.js';
import { FLAT_METADATA_FIELDS } from './form-definition.js';
import { findFirstFeatureStage, renderValue, userCanEdit } from './utils.js';
import { SHARED_STYLES } from '../css/shared-css.js';
import { customElement, state } from 'lit/decorators.js';
import { gateStateDisplayInfo } from './chromedash-gate-chip.js';
let preflightDialogEl;
export async function openPreflightDialog(user, feature, progress, process, action, stage, feStage, featureGates, showProceed = true) {
    if (!preflightDialogEl) {
        preflightDialogEl = document.createElement('chromedash-preflight-dialog');
        document.body.appendChild(preflightDialogEl);
        await preflightDialogEl.updateComplete;
    }
    return new Promise(resolve => {
        preflightDialogEl.openWithContext(user, feature, progress, process, action, stage, feStage, featureGates, resolve, showProceed);
    });
}
export function isPrereqReady(itemName, progress) {
    const vote = progress?.[itemName];
    return (vote != null &&
        (vote.state === PROGRESS_VOTE_STATE.VERIFIED ||
            vote.state === PROGRESS_VOTE_STATE.NA));
}
export function somePendingPrereqs(action, progress) {
    return action.prerequisites.some(itemName => !isPrereqReady(itemName, progress));
}
export function somePendingGates(featureGates, feStage) {
    return findPendingGates(featureGates, feStage).length > 0;
}
export function findOtherGates(featureGates, feStage) {
    const gatesForStage = featureGates.filter(g => g.stage_id == feStage.id);
    const otherGates = gatesForStage.filter(g => g.team_name != 'API Owners' && g.team_name != 'Data Quality');
    otherGates.sort((g1, g2) => GATE_TEAM_ORDER.indexOf(g1.team_name) -
        GATE_TEAM_ORDER.indexOf(g2.team_name));
    return otherGates;
}
export function findPendingGates(featureGates, feStage) {
    const otherGates = findOtherGates(featureGates, feStage);
    const pendingGates = otherGates.filter(g => !GATE_FINISHED_REVIEW_STATES.includes(g.state));
    return pendingGates;
}
let ChromedashPreflightDialog = class ChromedashPreflightDialog extends LitElement {
    constructor() {
        super(...arguments);
        this._resolve = () => {
            console.log('Missing resolve action');
        };
        this._showProceed = true;
        this._dirty = false;
        this._saving = false;
        this._touched = new Set();
    }
    static get styles() {
        return [
            ...SHARED_STYLES,
            css `
        h3 {
          margin: var(--content-padding) 0 var(--content-padding-quarter) 0;
          font-size: 16px;
          font-weight: 500;
        }

        .data-table {
          margin-bottom: var(--content-padding-half);
        }

        .data-table tbody:first-of-type tr:first-child td {
          border-top: none;
        }

        .data-table tr.feature-values,
        .data-table tr.feedback,
        .data-table tr.criteria {
          display: none;
        }

        .data-table tr.feature-values.always-show,
        .data-table tbody:has(details[open]) tr {
          display: table-row;
        }

        .data-table
          tbody:has(tr.feature-values.always-show)
          tr:first-child
          .edit-progress-item,
        .data-table
          tbody:has(details[open])
          tr:first-child
          .edit-progress-item {
          display: none;
        }

        .data-table tr.feature-value td,
        .data-table tr.feature-values td,
        .data-table tr.feedback td,
        .data-table tr.criteria td {
          border-top: none;
          animation: revealRow 200ms ease-out;
        }

        @keyframes revealRow {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .data-table td {
          vertical-align: middle;
          padding: 0 var(content-padding);
        }

        .data-table td:first-child {
          width: 1em;
        }

        .data-table td:first-child details {
          padding: 0;
        }

        .data-table td:first-child details summary {
          list-style-type: disclosure-closed;
          transition: transform 200ms ease-in-out;
        }

        .data-table td:first-child details[open] summary {
          margin-bottom: 0;
          transform: rotate(90deg);
        }

        .data-table td:nth-child(2) {
          width: 14em;
        }

        .data-table td:nth-child(3) {
          width: 40em;
        }

        .data-table td:last-child {
          width: 4em;
          text-align: right;
        }

        .status {
          display: inline-block;
          padding: 2px 8px;
          border-radius: var(--pill-border-radius);
          font-size: 0.9em;
        }

        .status.na,
        .status.not_applicable,
        .status.na_self-certified,
        .status.na_self-certified_then_verified,
        sl-select.na::part(combobox),
        sl-select.na::part(display-input) {
          background: var(--gate-not-applicable-background);
          color: var(--gate-not-applicable-color);
        }

        .status.preparing,
        .status.not_started,
        sl-select.not_started::part(combobox),
        sl-select.not_started::part(display-input) {
          background: var(--gate-preparing-background);
          color: var(--gate-preparing-color);
        }

        .status.pending,
        .status.ready_for_review,
        sl-select.ready_for_review::part(combobox),
        sl-select.ready_for_review::part(display-input) {
          background: var(--gate-pending-background);
          color: var(--gate-pending-color);
        }

        .status.needs_work,
        sl-select.needs_work::part(combobox),
        sl-select.needs_work::part(display-input) {
          background: var(--gate-needs-work-background);
          color: var(--gate-needs-work-color);
        }

        .status.approved,
        .status.verified,
        sl-select.verified::part(combobox),
        sl-select.verified::part(display-input) {
          background: var(--gate-approved-background);
          color: var(--gate-approved-color);
        }

        .status.denied {
          background: var(--gate-denied-background);
          color: var(--gate-denied-color);
        }

        .status.internal_review {
          background: var(--gate-pending-background);
          color: var(--gate-pending-color);
        }

        .status.na_requested {
          background: var(--gate-pending-background);
          color: var(--gate-pending-color);
        }

        sl-button {
          float: right;
          margin: var(--content-padding-half);
        }
      `,
        ];
    }
    openWithContext(user, feature, progress, process, action, stage, feStage, featureGates, resolve, showProceed) {
        this._user = user;
        this._feature = feature;
        this._progress = progress;
        this._process = process;
        this._action = action;
        this._stage = stage;
        this._feStage = feStage;
        this._featureGates = featureGates;
        this._resolve = resolve;
        this._showProceed = showProceed;
        this._dirty = false;
        this._touched = new Set();
        this.renderRoot
            .querySelectorAll('details[data-pi]')
            .forEach(detailsEl => {
            const itemName = detailsEl.dataset.pi || '';
            const vote = progress?.[itemName];
            detailsEl.open = Boolean(vote?.feedback);
        });
        this.renderRoot.querySelector('sl-dialog')?.show();
    }
    _fireEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            composed: true,
            detail,
        });
        this.dispatchEvent(event);
    }
    canVote() {
        if (this._user?.can_edit_all) {
            return true;
        }
        if (this._user?.can_review_release_notes) {
            return true;
        }
        if ((this._user?.approvable_gate_types || []).length > 0) {
            return true;
        }
        return false;
    }
    canEdit() {
        return userCanEdit(this._user, this._feature?.id);
    }
    hide() {
        this.renderRoot.querySelector('sl-dialog')?.hide();
    }
    handleCancel() {
        this.hide();
        this._resolve(false);
    }
    handleProceed() {
        // The button opens a new tab due to the href and target attrs.
        // Also, close this dialog, so it is gone when the user returns.
        this.hide();
        this._resolve(true);
    }
    renderEditLink(stage, feStage, pi) {
        // This function only renders links for progress items that have a field.
        if (!pi.field) {
            return nothing;
        }
        if (!this.canEdit()) {
            return nothing;
        }
        const isMetadataField = FLAT_METADATA_FIELDS.sections.some(section => pi.field !== undefined && section.fields.includes(pi.field));
        const pathSegment = !isMetadataField && stage && feStage ? `${feStage.id}` : 'metadata';
        return html `
      <a
        class="edit-progress-item"
        href="/guide/stage/${this._feature.id}/${pathSegment}#id_${pi.field}"
        @click=${this.hide}
      >
        Edit
      </a>
    `;
    }
    getStatusInfo(state) {
        let statusClass = 'not_started';
        let statusText = 'Not started';
        switch (state) {
            case PROGRESS_VOTE_STATE.READY_FOR_REVIEW:
                statusClass = 'ready_for_review';
                statusText = PROGRESS_VOTE_STATE_NAMES.READY_FOR_REVIEW;
                break;
            case PROGRESS_VOTE_STATE.VERIFIED:
                statusClass = 'verified';
                statusText = PROGRESS_VOTE_STATE_NAMES.VERIFIED;
                break;
            case PROGRESS_VOTE_STATE.NA:
                statusClass = 'na';
                statusText = PROGRESS_VOTE_STATE_NAMES.NA;
                break;
            case PROGRESS_VOTE_STATE.NEEDS_WORK:
                statusClass = 'needs_work';
                statusText = PROGRESS_VOTE_STATE_NAMES.NEEDS_WORK;
                break;
        }
        return { statusClass, statusText };
    }
    renderStatusChip(vote) {
        const { statusClass, statusText } = this.getStatusInfo(vote?.state);
        return html ` <span class="status ${statusClass}">${statusText}</span> `;
    }
    handleStatusMenuChange(e) {
        this._touched.add(e.target.dataset.pi);
        this._dirty = true;
        this.requestUpdate();
        if (e.target?.value == PROGRESS_VOTE_STATE.NEEDS_WORK) {
            const itemName = e.target?.dataset['pi'] || '';
            const detailsEl = this.renderRoot.querySelector(`details[data-pi="${itemName}"]`);
            if (detailsEl) {
                detailsEl.open = true;
            }
        }
    }
    handleFeedbackChange(e) {
        this._touched.add(e.target.dataset.pi);
        this._dirty = true;
    }
    handleSave() {
        this._saving = true;
        const promises = [];
        for (const prereq of this._touched) {
            const stateEl = this.renderRoot.querySelector(`.state-widget[data-pi="${prereq}"]`);
            const feedbackEl = this.renderRoot.querySelector(`.feedback-widget[data-pi="${prereq}"]`);
            if (stateEl) {
                const voteState = parseInt(stateEl.value);
                const feedback = feedbackEl ? feedbackEl.value.trim() : '';
                promises.push(window.csClient.postFeatureProgressVote(this._feature.id, prereq, voteState, feedback));
            }
        }
        Promise.all(promises).then(() => {
            this._saving = false;
            this.handleCancel();
            const app = document.querySelector('chromedash-app');
            if (app) {
                app.refetch();
            }
        });
    }
    renderStatusMenu(item, vote) {
        const stateEl = this.renderRoot.querySelector(`.state-widget[data-pi="${item.name}"]`);
        const state = this._touched.has(item.name) && stateEl
            ? parseInt(stateEl.value)
            : vote?.state || PROGRESS_VOTE_STATE.NOT_STARTED;
        const { statusClass } = this.getStatusInfo(state);
        return html `
      <sl-select
        value=${state}
        class="state-widget ${statusClass}"
        size="small"
        data-pi=${item.name}
        ?disabled=${this._saving}
        @sl-change="${this.handleStatusMenuChange}"
      >
        <sl-option value=${PROGRESS_VOTE_STATE.NOT_STARTED}
          >Not Started</sl-option
        >
        <sl-option value=${PROGRESS_VOTE_STATE.READY_FOR_REVIEW}
          >Ready for review</sl-option
        >
        <sl-option value=${PROGRESS_VOTE_STATE.NEEDS_WORK}
          >Needs work</sl-option
        >
        <sl-option value=${PROGRESS_VOTE_STATE.VERIFIED}>Verified</sl-option>
        <sl-option value=${PROGRESS_VOTE_STATE.NA}>N/A</sl-option>
      </sl-select>
    `;
    }
    renderProgressItem(stage, feStage, item) {
        const vote = this._progress?.[item.name];
        const alwaysShowValueRow = this.canVote() && vote?.state === PROGRESS_VOTE_STATE.READY_FOR_REVIEW;
        const statusRow = html `
      <tr>
        <td>
          <details data-pi=${item.name} ?open=${Boolean(vote?.feedback)}>
            <summary aria-label="Toggle details for ${item.name}"></summary>
          </details>
        </td>
        <td>
          ${this.canVote() ? this.renderStatusMenu(item, vote) : this.renderStatusChip(vote)}
        </td>
        <td>${item.description || item.name}</td>
        <td>${this.renderEditLink(stage, feStage, item)}</td>
      </tr>
    `;
        let valueRow = html `${nothing}`;
        if (item.field) {
            const fieldDisplayName = makeDisplaySpec(item.field)[1];
            valueRow = html `
        <tr class="feature-values ${alwaysShowValueRow ? 'always-show' : ''}">
          <td></td>
          <td>${fieldDisplayName}:</td>
          <td>${renderValue(feStage, this._feature, item.field)}</td>
          <td>${this.renderEditLink(stage, feStage, item)}</td>
        </tr>
      `;
        }
        const feedbackWidget = html ` <sl-input
      class="feedback-widget"
      size="small"
      data-pi=${item.name}
      @sl-change="${this.handleFeedbackChange}"
      value=${vote?.feedback || ''}
    ></sl-input>`;
        const feedbackRow = vote?.feedback || this.canVote()
            ? html `
            <tr class="feedback">
              <td></td>
              <td>Feedback:</td>
              <td>${this.canVote() ? feedbackWidget : vote?.feedback}</td>
              <td></td>
            </tr>
          `
            : nothing;
        const criteriaRow = item.criteria
            ? html `
          <tr class="criteria">
            <td></td>
            <td>Criteria:</td>
            <td>${item.criteria}</td>
            <td></td>
          </tr>
        `
            : nothing;
        return html `
      <tbody>
        ${statusRow} ${valueRow} ${feedbackRow} ${criteriaRow}
      </tbody>
    `;
    }
    renderStageTable(stage, prereqItems) {
        if (prereqItems.length === 0) {
            return nothing;
        }
        const feStage = findFirstFeatureStage(stage.stage_type, this._stage, this._feature);
        return html `
      <h3>${stage.name}</h3>
      <table class="data-table">
        ${prereqItems.map(item => this.renderProgressItem(stage, feStage, item))}
      </table>
    `;
    }
    renderGateState(state) {
        let { stateName, className, statusIconName, abbrev } = gateStateDisplayInfo(state);
        if (stateName === 'Preparing') {
            stateName = 'Not started';
            abbrev = 'Not started';
        }
        return html `
      <span class="status ${className}" title="${stateName}">${abbrev}</span>
    `;
    }
    renderGatesTable(otherGates) {
        if (otherGates.length === 0) {
            return nothing;
        }
        return html `
      <h3>Other gates</h3>
      <table class="data-table">
        ${otherGates.map(g => html `
            <tr>
              <td></td>
              <td>${this.renderGateState(g.state)}</td>
              <td>${g.team_name}</td>
              <td>
                <a
                  href="/feature/${this._feature.id}?gate=${g.id}"
                  @click=${this.hide}
                  >View</a
                >
              </td>
            </tr>
          `)}
      </table>
    `;
    }
    renderDialogContent() {
        if (this._feature == null) {
            return nothing;
        }
        const prereqNames = new Set(this._action.prerequisites || []);
        const seenPrereqs = new Set();
        const stageTables = (this._process?.stages || []).map(s => {
            const stagePrereqs = (s.progress_items || []).filter(pi => {
                // Note that metadata-related PIs are associated with the
                // first stage in the process.
                if (prereqNames.has(pi.name) && !seenPrereqs.has(pi.name)) {
                    seenPrereqs.add(pi.name);
                    return true;
                }
                return false;
            });
            return this.renderStageTable(s, stagePrereqs);
        });
        const otherGates = findOtherGates(this._featureGates, this._feStage);
        const saveText = this._saving ? 'Saving...' : 'Save';
        const saveButton = html `
      <sl-button
        size="small"
        @click=${this.handleSave}
        variant="primary"
        ?disabled=${this._saving || !this._dirty}
        >${saveText}
      </sl-button>
    `;
        return html `
      Please address any relevant "Not started" or "Needs work" items before
      requesting review. ${stageTables} ${this.renderGatesTable(otherGates)}
      ${this._showProceed
            ? html ` <sl-button size="small" @click=${this.handleProceed}
              >Proceed</sl-button
            >`
            : nothing}
      <sl-button size="small" variant="warning" @click=${this.handleCancel}
        >Cancel</sl-button
      >
      ${this.canVote() ? saveButton : nothing}
    `;
    }
    render() {
        return html `
      <sl-dialog
        class="missing-prereqs"
        label="Checklist: ${this._action?.name}"
        style="--width:fit-content"
      >
        ${this.renderDialogContent()}
      </sl-dialog>
    `;
    }
};
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_user", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_feature", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_featureGates", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_progress", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_process", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_action", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_stage", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_feStage", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_resolve", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_showProceed", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_dirty", void 0);
__decorate([
    state()
], ChromedashPreflightDialog.prototype, "_saving", void 0);
ChromedashPreflightDialog = __decorate([
    customElement('chromedash-preflight-dialog')
], ChromedashPreflightDialog);
export { ChromedashPreflightDialog };
